// frontend/src/components/TopNav.js
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-06-02
// Updated:      2026-09-24
//
// Description:  Full-width top navigation bar with user flyout, theme, logout support,
//               alert bell with counter, and Portal-aligned compact mobile header.
//
// Mobile:
//               • Uses Layout.js as the authoritative mobile breakpoint source.
//               • Replaces the desktop header presentation with a compact branded bar.
//               • Provides mobile alert access in the top navigation.
//               • The duplicate mobile profile icon has been removed from the top navigation;
//                 mobile profile access is owned by the bottom action bar.
//               • Mobile alert control uses the same AlertContext count and AlertFlyout
//                 presentation as desktop; no duplicate alert state is introduced.
//               • Opening alerts closes the profile flyout and mobile navigation drawer.
//               • Provides the mobile navigation hamburger callback owned by Layout.js.
//               • The bottom action bar still opens the existing right-side mobile
//                 ProfileFlyout via the scoped vision:open-profile event.
//               • Does not duplicate navigation definitions or mount the mobile drawer here.
//               • Desktop presentation and behaviour remain unchanged.
//
// PWA-003 (2026-09-23):
//               • Listens for the scoped vision:open-profile event emitted by
//                 the mobile bottom navigation.
//               • Reuses the existing mobile ProfileFlyout and closes alerts
//                 and the mobile drawer before opening it.

import React, { useState, useRef, useEffect, useMemo } from "react";
import { FaCog, FaUserCircle, FaBell, FaBars } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import { useAuth } from "../context/AuthContext";
import { useAlerts } from "../context/AlertContext";

import ProfileFlyout from "./ProfileFlyout";
import ManageUserModal from "./ManageUserModal";
import AlertFlyout from "./AlertFlyout";

const DESKTOP_HEADER_MIN_HEIGHT = 104;
const MOBILE_HEADER_MIN_HEIGHT = 64;

const TopNav = ({
  isMobile = false,
  mobileMenuOpen = false,
  onOpenMobileMenu,
  onCloseMobileMenu,
}) => {
  const { theme } = useTheme();
  const { user, logout } = useAuth();
  const { alerts } = useAlerts();

  const navigate = useNavigate();
  const logoUrl = theme.logo_header_url;

  const [hover, setHover] = useState(false);
  const [active, setActive] = useState(false);
  const [showFlyout, setShowFlyout] = useState(false);
  const [showAlerts, setShowAlerts] = useState(false);
  const [manageUserId, setManageUserId] = useState(null);

  const profileRef = useRef(null);
  const alertsRef = useRef(null);

  const alertCount = useMemo(
    () => (Array.isArray(alerts) ? alerts.length : 0),
    [alerts]
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      const t = event?.target;
      const inProfile =
        profileRef.current && profileRef.current.contains(t);
      const inAlerts =
        alertsRef.current && alertsRef.current.contains(t);

      if (!inProfile && !inAlerts) {
        setShowFlyout(false);
        setShowAlerts(false);
      }
    };

    document.addEventListener("pointerdown", handleClickOutside);
    return () =>
      document.removeEventListener("pointerdown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isMobile) {
      setShowFlyout(false);
      setShowAlerts(false);
    }
  }, [isMobile]);

  useEffect(() => {
    if (!isMobile) {
      return undefined;
    }

    const handleOpenProfile = () => {
      setShowAlerts(false);

      if (mobileMenuOpen && typeof onCloseMobileMenu === "function") {
        onCloseMobileMenu();
      }

      setShowFlyout(true);
    };

    window.addEventListener("vision:open-profile", handleOpenProfile);
    return () => {
      window.removeEventListener("vision:open-profile", handleOpenProfile);
    };
  }, [isMobile, mobileMenuOpen, onCloseMobileMenu]);

  const getButtonColor = () => {
    if (active) return theme.button_action?.click || "#000";
    if (hover) return theme.button_action?.hover || "#000";
    return theme.button_action?.normal || "#000";
  };

  const handleMobileMenuClick = () => {
    setShowFlyout(false);
    setShowAlerts(false);

    if (mobileMenuOpen) {
      if (typeof onCloseMobileMenu === "function") {
        onCloseMobileMenu();
      }
      return;
    }

    if (typeof onOpenMobileMenu === "function") {
      onOpenMobileMenu();
    }
  };

  const handleMobileAlertsClick = () => {
    setShowFlyout(false);

    if (mobileMenuOpen && typeof onCloseMobileMenu === "function") {
      onCloseMobileMenu();
    }

    setShowAlerts((visible) => !visible);
  };

  const handleManageProfile = () => {
    if (!user?.id) return;

    setShowFlyout(false);
    setShowAlerts(false);

    if (mobileMenuOpen && typeof onCloseMobileMenu === "function") {
      onCloseMobileMenu();
    }

    setManageUserId(user.id);
  };

  if (isMobile) {
    return (
      <>
        <header
          style={{
            width: "100%",
            minHeight: `${MOBILE_HEADER_MIN_HEIGHT}px`,
            flex: "0 0 auto",

            backgroundColor:
              theme.primary_color || "var(--primary-color)" || "#1a1a1a",
            borderBottom: "1px solid var(--accent-color)",

            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",

            padding: "8px 14px",

            boxSizing: "border-box",

            position: "sticky",
            top: 0,
            zIndex: 1000,

            color: "var(--secondary-color)",
            fontFamily: "var(--font-header)",

            boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              minWidth: 0,
              flex: "1 1 auto",
            }}
          >
            {logoUrl && (
              <div
                style={{
                  width: "92px",
                  height: "46px",
                  flex: "0 0 auto",

                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",

                  overflow: "hidden",
                }}
              >
                <img
                  src={logoUrl}
                  alt="TopNav Logo"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    width: "auto",
                    height: "auto",
                    objectFit: "contain",
                    objectPosition: "left center",
                    display: "block",
                  }}
                />
              </div>
            )}

            <div
              style={{
                minWidth: 0,
                marginLeft: logoUrl ? "10px" : 0,

                fontSize: "1rem",
                fontWeight: 700,
                lineHeight: 1.15,

                color: "var(--secondary-color)",

                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              Vision
            </div>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              flex: "0 0 auto",
              gap: "8px",
              marginLeft: "10px",
            }}
          >
            <div
              ref={alertsRef}
              style={{
                position: "relative",
                width: "46px",
                height: "46px",
                flex: "0 0 46px",
              }}
            >
              <button
                type="button"
                onMouseDown={(event) => event.stopPropagation()}
                onClick={handleMobileAlertsClick}
                aria-label={
                  alertCount > 0
                    ? `${showAlerts ? "Close" : "Open"} alerts, ${alertCount} active`
                    : `${showAlerts ? "Close" : "Open"} alerts`
                }
                aria-expanded={showAlerts}
                title="View Alerts"
                style={{
                  width: "46px",
                  height: "46px",
                  flex: "0 0 46px",

                  padding: 0,

                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",

                  border: "none",
                  borderRadius: "8px",

                  background: "transparent",
                  color: "var(--accent-color)",

                  cursor: "pointer",
                  touchAction: "manipulation",

                  position: "relative",
                }}
              >
                <FaBell size={26} />

                {alertCount > 0 && (
                  <span
                    aria-hidden="true"
                    style={{
                      position: "absolute",
                      bottom: 5,
                      right: 3,
                      minWidth: "18px",
                      height: "18px",

                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",

                      padding: "0 4px",

                      backgroundColor: "var(--accent-color)",
                      color: "var(--primary-color)",

                      borderRadius: "999px",
                      border: "1px solid white",

                      boxSizing: "border-box",

                      fontSize: "0.62rem",
                      fontWeight: "bold",
                      lineHeight: 1,

                      boxShadow: "0 0 2px rgba(0,0,0,0.2)",
                    }}
                  >
                    {alertCount}
                  </span>
                )}
              </button>

              {showAlerts && (
                <AlertFlyout
                  onClose={() => setShowAlerts(false)}
                />
              )}
            </div>

            <button
              type="button"
              onClick={handleMobileMenuClick}
              aria-label={
                mobileMenuOpen
                  ? "Close navigation menu"
                  : "Open navigation menu"
              }
              aria-expanded={mobileMenuOpen}
              title={
                mobileMenuOpen
                  ? "Close navigation menu"
                  : "Open navigation menu"
              }
              style={{
                width: "48px",
                height: "48px",
                flex: "0 0 48px",

                padding: 0,

                display: "flex",
                alignItems: "center",
                justifyContent: "center",

                border: "none",
                borderRadius: "8px",

                background: "transparent",
                color: "var(--accent-color)",

                cursor: "pointer",
                touchAction: "manipulation",
              }}
            >
              <FaBars size={28} />
            </button>
          </div>
        </header>

        {showFlyout && (
          <>
            <div
              role="presentation"
              aria-hidden="true"
              onClick={() => setShowFlyout(false)}
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.4)",
                zIndex: 150550,
              }}
            />

            <aside
              ref={profileRef}
              role="dialog"
              aria-modal="true"
              aria-label="User profile"
              style={{
                position: "fixed",
                right: 0,
                top: 0,
                height: "100dvh",
                width: "min(420px, 92vw)",
                zIndex: 150601,
                display: "flex",
                alignItems: "flex-start",
                overflow: "hidden",
              }}
            >
              <ProfileFlyout
                mobile
                onClose={() => setShowFlyout(false)}
                onManageProfile={handleManageProfile}
              />
            </aside>
          </>
        )}

        {manageUserId && (
          <ManageUserModal
            userId={manageUserId}
            onClose={() => setManageUserId(null)}
          />
        )}
      </>
    );
  }

  return (
    <header
      style={{
        width: "100%",
        backgroundColor: theme.primary_color || "#1a1a1a",
        borderBottom: "1px solid var(--accent-color)",
        padding: "12px 24px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        minHeight: `${DESKTOP_HEADER_MIN_HEIGHT}px`,
        borderTopLeftRadius: "12px",
        borderTopRightRadius: "12px",
        boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
        position: "sticky",
        top: 0,
        zIndex: 1000,
        boxSizing: "border-box",
        color: "var(--secondary-color)",
        fontFamily: "var(--font-header)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center" }}>
        {/* Enlarged logo container: 260x100 */}
        <div
          style={{
            width: "260px",
            height: "100px",
            backgroundColor: theme.primary_color || "#1a1a1a",
            borderRadius: "8px",
            marginRight: "0.75rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          {logoUrl && (
            <img
              src={logoUrl}
              alt="TopNav Logo"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
            />
          )}
        </div>

        <h1
          style={{
            fontSize: "1.5rem",
            fontWeight: "bold",
            margin: 0,
          }}
        >
          {theme.client_name || "Vision"} Systems
        </h1>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "1.2rem",
          position: "relative",
        }}
      >
        <FaCog
          size={32}
          style={{
            cursor: "pointer",
            color: "var(--accent-color)",
          }}
          title="Settings"
          onClick={() => navigate("/dashboard/system")}
        />

        {/* Bell Icon and Counter (Context-driven) */}
        <div
          ref={alertsRef}
          style={{
            position: "relative",
            cursor: "pointer",
          }}
          onClick={() => setShowAlerts((v) => !v)}
          title="View Alerts"
        >
          <FaBell
            size={32}
            style={{
              color: "var(--accent-color)",
            }}
          />

          {alertCount > 0 && (
            <span
              style={{
                position: "absolute",
                bottom: -3,
                right: -5,
                backgroundColor: "var(--accent-color)",
                color: "var(--primary-color)",
                borderRadius: "999px",
                fontSize: "0.65rem",
                fontWeight: "bold",
                padding: "2px 6px",
                lineHeight: 1,
                border: "1px solid white",
                boxShadow: "0 0 2px rgba(0,0,0,0.2)",
              }}
            >
              {alertCount}
            </span>
          )}

          {showAlerts && (
            <AlertFlyout
              onClose={() => setShowAlerts(false)}
            />
          )}
        </div>

        {/* User Info */}
        <div style={{ textAlign: "right" }}>
          <div
            style={{
              fontSize: "0.9rem",
              color: "var(--secondary-color)",
            }}
          >
            Logged in as:
          </div>
          <div
            style={{
              fontWeight: "bold",
              color: "var(--accent-color)",
            }}
          >
            {user?.first_name} {user?.last_name}
          </div>
        </div>

        {/* Profile Flyout */}
        <div style={{ position: "relative" }}>
          <FaUserCircle
            size={32}
            title="User Profile"
            style={{
              color: "var(--accent-color)",
              cursor: "pointer",
            }}
            onClick={() => setShowFlyout((v) => !v)}
          />

          {showFlyout && (
            <div
              ref={profileRef}
              style={{
                position: "fixed",
                top: `${DESKTOP_HEADER_MIN_HEIGHT}px`,
                right: "1.5rem",
                zIndex: 2000,
              }}
            >
              <ProfileFlyout
                onClose={() => setShowFlyout(false)}
                onManageProfile={handleManageProfile}
              />
            </div>
          )}
        </div>

        <button
          onClick={logout}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => {
            setHover(false);
            setActive(false);
          }}
          onMouseDown={() => setActive(true)}
          onMouseUp={() => setActive(false)}
          style={{
            backgroundColor: getButtonColor(),
            color: "white",
            border: "none",
            borderRadius: "6px",
            padding: "6px 12px",
            fontSize: "0.85rem",
            cursor: "pointer",
            fontFamily: "var(--font-body)",
            transition: "background-color 0.2s ease-in-out",
          }}
        >
          Logout
        </button>
      </div>

      {manageUserId && (
        <ManageUserModal
          userId={manageUserId}
          onClose={() => setManageUserId(null)}
        />
      )}
    </header>
  );
};

export default TopNav;
